import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParent } from '../../contexts/parentContext';
import ProfileImage from '../../components/profileImage';
import api from '../../services/api';

const ChildControlFull = () => {
  const { t } = useTranslation();
  const { selectedChild } = useParent();
  const [childData, setChildData] = useState(null);
  const [events, setEvents] = useState([]);
  const [posts, setPosts] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    if (selectedChild) {
      fetchChildData();
    }
  }, [selectedChild]);

  const fetchChildData = async () => {
    try {
      setLoading(true);
      
      // Fetch child's events, posts, and team data
      const [eventsRes, postsRes] = await Promise.all([
        api.get(`/event/get-events-for-player/${selectedChild._id}`),
        api.get(`/feed/get-posts-by-user/${selectedChild._id}`)
      ]);

      setEvents(eventsRes.data?.events || []);
      setPosts(postsRes.data?.posts || []);
      
      // If child has team, get team members
      if (selectedChild.metaData?.team) {
        const teamRes = await api.get(`/team/get-team-members/${selectedChild.metaData.team}`);
        setTeamMembers(teamRes.data?.members || []);
      }
      
    } catch (err) {
      console.error('Error fetching child data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedChild) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Child Selected</h2>
          <p className="text-gray-600">Please select a child first.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header with child info */}
        <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl shadow-xl overflow-hidden mb-8">
          <div className="p-8 text-white">
            <div className="flex items-center space-x-6">
              <ProfileImage
                image={selectedChild.profilePicture}
                alt={selectedChild.firstName}
                className="w-24 h-24 rounded-full object-cover border-4 border-white/20"
              />
              <div>
                <h1 className="text-4xl font-bold mb-2">
                  {selectedChild.firstName} {selectedChild.lastName}
                </h1>
                <p className="text-orange-100 text-lg">@{selectedChild.userName}</p>
                <p className="text-orange-100 text-sm mt-1">{t('parent.fullControl')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {['overview', 'events', 'feed', 'team'].map((section) => (
                <button
                  key={section}
                  onClick={() => setActiveSection(section)}
                  className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                    activeSection === section
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {t(`parent.${section}`)}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Section */}
            {activeSection === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                  <h3 className="text-xl font-semibold mb-4">{t('parent.childInformation')}</h3>
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">{t('parent.fullName')}</p>
                        <p className="font-medium">{selectedChild.firstName} {selectedChild.lastName}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{t('parent.username')}</p>
                        <p className="font-medium">@{selectedChild.userName}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{t('parent.email')}</p>
                        <p className="font-medium">{selectedChild.email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{t('parent.accountStatus')}</p>
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          selectedChild.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {selectedChild.isActive ? t('parent.active') : t('parent.inactive')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-4">{t('parent.quickStats')}</h3>
                  <div className="space-y-4">
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-orange-600">{t('parent.totalEvents')}</p>
                          <p className="text-2xl font-bold text-orange-800">{events.length}</p>
                        </div>
                        <div className="w-12 h-12 bg-orange-200 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zM4 7h12v1H4V7z"/>
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-blue-600">{t('parent.totalPosts')}</p>
                          <p className="text-2xl font-bold text-blue-800">{posts.length}</p>
                        </div>
                        <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z"/>
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-green-600">{t('parent.teamMembers')}</p>
                          <p className="text-2xl font-bold text-green-800">{teamMembers.length}</p>
                        </div>
                        <div className="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Events Section */}
            {activeSection === 'events' && (
              <div>
                <h3 className="text-xl font-semibold mb-4">{t('parent.childEvents')}</h3>
                {events.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zM4 7h12v1H4V7z"/>
                      </svg>
                    </div>
                    <h4 className="text-lg font-medium text-gray-900 mb-2">{t('parent.noEvents')}</h4>
                    <p className="text-gray-500">{t('parent.noEventsDescription')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {events.map((event) => (
                      <div key={event._id} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                        <h4 className="font-semibold text-gray-900 mb-2">{event.title}</h4>
                        <p className="text-sm text-gray-600 mb-3">{event.description}</p>
                        <div className="flex items-center text-sm text-gray-500">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zM4 7h12v1H4V7z"/>
                          </svg>
                          {new Date(event.date).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Feed Section */}
            {activeSection === 'feed' && (
              <div>
                <h3 className="text-xl font-semibold mb-4">{t('parent.childPosts')}</h3>
                {posts.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z"/>
                      </svg>
                    </div>
                    <h4 className="text-lg font-medium text-gray-900 mb-2">{t('parent.noPosts')}</h4>
                    <p className="text-gray-500">{t('parent.noPostsDescription')}</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {posts.map((post) => (
                      <div key={post._id} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                        <div className="flex items-center space-x-3 mb-4">
                          <ProfileImage
                            image={selectedChild.profilePicture}
                            alt={selectedChild.firstName}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              {selectedChild.firstName} {selectedChild.lastName}
                            </h4>
                            <p className="text-sm text-gray-500">
                              {new Date(post.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <p className="text-gray-800 mb-4">{post.content}</p>
                        {post.images && post.images.length > 0 && (
                          <div className="grid grid-cols-2 gap-2 mb-4">
                            {post.images.map((image, index) => (
                              <img
                                key={index}
                                src={image}
                                alt="Post content"
                                className="w-full h-32 object-cover rounded-lg"
                              />
                            ))}
                          </div>
                        )}
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span>{post.likes?.length || 0} {t('parent.likes')}</span>
                          <span>{post.comments?.length || 0} {t('parent.comments')}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Team Section */}
            {activeSection === 'team' && (
              <div>
                <h3 className="text-xl font-semibold mb-4">{t('parent.teamMembers')}</h3>
                {teamMembers.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z"/>
                      </svg>
                    </div>
                    <h4 className="text-lg font-medium text-gray-900 mb-2">{t('parent.noTeamMembers')}</h4>
                    <p className="text-gray-500">{t('parent.noTeamMembersDescription')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {teamMembers.map((member) => (
                      <div key={member._id} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center space-x-4 mb-4">
                          <ProfileImage
                            image={member.profilePicture}
                            alt={member.firstName}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              {member.firstName} {member.lastName}
                            </h4>
                            <p className="text-sm text-gray-500">@{member.userName}</p>
                            <p className="text-xs text-gray-400">{member.accountType}</p>
                          </div>
                        </div>
                        <button className="w-full px-3 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm">
                          {t('parent.startChat')}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChildControlFull; 